/**
 * ChatFullAutoConfirmCard — natural-language confirmation card per spec
 * section 3 decision #8.
 *
 * Slash commands bypass this card; only LLM-classified FULL_AUTO intents
 * surface this gate. Three buttons emit `fullauto.confirm`,
 * `fullauto.edit`, or `fullauto.cancel` semantically.
 *
 * Phase 3 - Task 17.
 */

import type { ReactElement } from 'react';

export interface ChatFullAutoConfirmCardProps {
  readonly packageName: string;
  readonly summary: string;
  readonly originalPrompt: string;
  readonly estimatedDurationMin: number;
  readonly decided?: boolean;
  readonly onConfirm: (args: { originalPrompt: string }) => void;
  readonly onEdit: () => void;
  readonly onCancel: () => void;
}

export function ChatFullAutoConfirmCard({
  packageName,
  summary,
  originalPrompt,
  estimatedDurationMin,
  decided = false,
  onConfirm,
  onEdit,
  onCancel,
}: ChatFullAutoConfirmCardProps): ReactElement {
  return (
    <article
      data-testid="fullauto-confirm"
      className="rounded-lg border border-amber-700/40 bg-amber-950/10 p-3"
    >
      <header className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-amber-300">
          Confirm Full-Auto
        </span>
        <span className="rounded bg-zinc-800 px-2 py-0.5 text-xs text-zinc-300">
          {packageName}
        </span>
      </header>
      <p className="mt-2 text-zinc-100">{summary}</p>
      <p className="mt-2 text-xs text-zinc-500">
        Estimated runtime: ~{estimatedDurationMin} min
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={decided}
          onClick={() => {
            onConfirm({ originalPrompt });
          }}
          className="rounded bg-emerald-600 px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          Yes, run it
        </button>
        <button
          type="button"
          disabled={decided}
          onClick={onEdit}
          className="rounded border border-zinc-700 px-3 py-1 text-sm text-zinc-200 disabled:opacity-50"
        >
          Edit
        </button>
        <button
          type="button"
          disabled={decided}
          onClick={onCancel}
          className="rounded border border-zinc-700 px-3 py-1 text-sm text-zinc-300 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </article>
  );
}

export default ChatFullAutoConfirmCard;
